import { cookieUtils, COOKIE_KEY } from '@app/app/cookie';

import { SUPPORTED_LANGUAGE_CODES } from '../constants';

/**
 * Утилиты для работы с кукой языка
 * Кука называется 'lang', срок жизни 1 год
 * Бэкенд устанавливает ту же куку 'lang' - она имеет приоритет
 */

/**
 * Получает язык из куки (lang) - используется и бэкендом, и фронтендом
 * @returns Код языка или null, если кука не установлена или язык не поддерживается
 */
export const getLanguageFromCookie = (): string | null => {
  const lang = cookieUtils.get(COOKIE_KEY.language);
  if (lang && SUPPORTED_LANGUAGE_CODES.includes(lang)) {
    return lang;
  }
  return null;
};

// Флаг для предотвращения циклических обновлений
// Используется когда мы устанавливаем куку программно, чтобы не триггерить лишние обновления
let isSettingCookieProgrammatically = false;

/**
 * Устанавливает язык в куку
 * Кука называется 'lang', срок жизни 1 год (как требует бэкенд)
 * Бэкенд также устанавливает эту куку, поэтому нужно быть аккуратным с обновлениями
 * @param langCode - Код языка
 * @param skipUpdateCheck - Пропустить проверку текущей куки (для внутренних обновлений)
 */
export const setLanguageCookie = (langCode: string, skipUpdateCheck = false): void => {
  // Проверяем, что язык поддерживается
  if (!SUPPORTED_LANGUAGE_CODES.includes(langCode)) {
    console.warn(`Language ${langCode} is not supported, falling back to 'en'`);
    langCode = 'en';
  }

  // Если кука уже установлена с тем же значением - не обновляем (избегаем лишних обновлений)
  if (!skipUpdateCheck && !isSettingCookieProgrammatically) {
    const currentLang = getLanguageFromCookie();
    if (currentLang === langCode) {
      // Кука уже установлена с нужным значением, не обновляем
      return;
    }
  }

  // Устанавливаем куку со сроком жизни 1 год (требование бэкенда)
  const oneYearFromNow = new Date();
  oneYearFromNow.setFullYear(oneYearFromNow.getFullYear() + 1);

  cookieUtils.set(COOKIE_KEY.language, langCode, {
    expires: oneYearFromNow,
  });
};

/**
 * Устанавливает флаг для предотвращения циклических обновлений при программной установке куки
 */
export const setProgrammaticCookieFlag = (value: boolean): void => {
  isSettingCookieProgrammatically = value;
};

/**
 * Удаляет куку языка
 */
export const removeLanguageCookie = (): void => {
  cookieUtils.remove(COOKIE_KEY.language);
};
